import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { CallistoLogo } from "../components/CallistoLogo";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (pw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset successful. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-callisto-app p-6">
        <div className="bg-white rounded-2xl border border-callisto shadow-sm p-8 text-center max-w-md w-full">
          <h1 className="text-xl font-bold text-callisto-navy">Invalid reset link</h1>
          <p className="mt-2 text-sm text-callisto-muted">
            The reset link is missing a token. Please request a new one.
          </p>
          <Link
            to="/forgot-password"
            className="mt-5 inline-block text-sm font-semibold text-callisto-gold hover:underline"
          >
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-callisto-app">
      <div className="px-6 py-5 bg-white border-b border-callisto">
        <CallistoLogo variant="light" size="md" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl border border-callisto shadow-sm p-8">
          <div className="h-12 w-12 rounded-xl bg-callisto-gold/10 flex items-center justify-center mb-4">
            <ShieldCheck className="h-6 w-6 text-callisto-gold" />
          </div>
          <h1 className="text-2xl font-bold text-callisto-navy">Set a new password</h1>
          <p className="mt-1 text-sm text-callisto-muted">
            Choose a strong password (minimum 8 characters).
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
            <div className="space-y-2">
              <Label htmlFor="rp-pw" className="text-callisto-navy font-medium">
                New password
              </Label>
              <Input
                id="rp-pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                minLength={8}
                className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="reset-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rp-pw2" className="text-callisto-navy font-medium">
                Confirm password
              </Label>
              <Input
                id="rp-pw2"
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                minLength={8}
                className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="reset-password-confirm-input"
              />
            </div>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
              data-testid="reset-password-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
